import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface SystemConfig {
    facility: {
        latitude: string;
        longitude: string;
    };
    financial: {
        graceMinutes: number;     // Check-in arrival grace (NOT detention free time)
        detentionRate: number;    // Display-only fallback. Authoritative source: warehouse DB
        currency: 'USD' | 'CAD';
    };
    units: {
        weight: 'LBS' | 'KG';
        distance: 'MI' | 'KM';
    };
    appointments: {
        defaultDurationMins: number; // e.g. 60
        bufferBeforeMins: number;    // e.g. 15
        bufferAfterMins: number;     // e.g. 15
        lateAfterMins: number;       // e.g. 15 (if arrived 15m after start = late)
        geofenceRequired: boolean;
        geofenceRadiusKm: number;    // e.g. 0.5
    };
    notifications: {
        emailLate: boolean;
        emailDwellBreach: boolean;
        emailExceptions: boolean;
        dailySummary: boolean;
    };
    appearance: {
        theme: 'dark' | 'light';
        density: 'comfortable' | 'compact';
    };
    defaults: {
        startPage: '/dashboard' | '/docks' | '/appointments' | '/orders' | '/carriers' | '/analytics';
        appointmentsDefaultView: 'day' | 'week';
        dockSort: 'number' | 'status' | 'activity';
        autoRefreshSeconds: number; // 0 (off) to 3600
    };
    safety: {
        confirmDestructiveActions: boolean;
    };
}

interface SystemConfigState {
    config: SystemConfig;
    updateSection: <K extends keyof SystemConfig>(section: K, updates: Partial<SystemConfig[K]>) => void;
    resetDefaults: () => void;
}

const DEFAULT_CONFIG: SystemConfig = {
    facility: {
        latitude: '',
        longitude: ''
    },
    financial: {
        graceMinutes: 15,       // Check-in arrival grace (NOT detention free time)
        detentionRate: 60,      // Display-only fallback; authoritative source: warehouse DB
        currency: 'USD'
    },
    units: {
        weight: 'LBS',
        distance: 'MI'
    },
    appointments: {
        defaultDurationMins: 60,
        bufferBeforeMins: 0,
        bufferAfterMins: 0,
        lateAfterMins: 15,
        geofenceRequired: false,
        geofenceRadiusKm: 0.5
    },
    notifications: {
        emailLate: true,
        emailDwellBreach: true,
        emailExceptions: true,
        dailySummary: false
    },
    appearance: {
        theme: 'dark',
        density: 'comfortable'
    },
    defaults: {
        startPage: '/dashboard',
        appointmentsDefaultView: 'day',
        dockSort: 'number',
        autoRefreshSeconds: 0
    },
    safety: {
        confirmDestructiveActions: true
    }
};

export const useSystemConfig = create<SystemConfigState>()(
    persist(
        (set) => ({
            // Use JSON parse/stringify to ensure initial state is a deep copy, not reference
            config: JSON.parse(JSON.stringify(DEFAULT_CONFIG)),
            updateSection: (section, updates) => set((state) => ({
                config: {
                    ...state.config,
                    [section]: { ...state.config[section], ...updates }
                }
            })),
            resetDefaults: () => set({ config: JSON.parse(JSON.stringify(DEFAULT_CONFIG)) })
        }),
        {
            name: 'baywhiz_system_config_v1'
        }
    )
);
